/**
 * The numbers the Providers page is built from, kept apart from the markup so
 * a test can check them rather than a sentence.
 *
 * Every figure here mirrors a constant in the engine, and none of them is
 * held in step by hand: the scalars below are checked by
 * `scripts/check-engine-constants.mjs`, and `THREAD_LIMITS` by
 * `Providers.test.tsx`, both of which read the Rust source directly. The
 * engine is the source of truth, and when the two disagree it is this file
 * that is wrong.
 */

/**
 * The engine's own ceiling on a pool, upload and restore alike. It is about
 * memory in this process, not about any destination, which is why every
 * backend's ceiling is capped by it: `MAX_UPLOAD_CONCURRENCY` and
 * `MAX_RESTORE_CONCURRENCY` in the engine crate, `MAX_BACKUP_THREADS` and
 * `MAX_RESTORE_THREADS` in the desktop app.
 *
 * One constant for two Rust ones that are equal today. The check refuses to
 * pick a side if they ever part company, and says to split this instead.
 */
export const ENGINE_CEILING = 16;

/** The bounds and default of the pack-size field, in MiB. Mirrors
 * `MIN_TARGET_PACK_SIZE`, `MAX_TARGET_PACK_SIZE` and `DEFAULT_TARGET_PACK_SIZE`
 * in the format crate, and the desktop app's `StorageLocationView` picker. */
export const MIN_PACK_MIB = 16;
export const MAX_PACK_MIB = 1024;
export const DEFAULT_PACK_MIB = 64;

/** Metadata packs are always this size, whatever the data pack target is:
 * `META_PACK_TARGET_SIZE` in the format crate. It shows up in the memory maths
 * as the small second pack every producer and every restore worker holds. */
export const META_PACK_MIB = 16;

/** The parity the app adds by default, as a percentage of each pack:
 * `DEFAULT_REDUNDANCY_PERCENT` in the parity crate. */
export const DEFAULT_PARITY_PERCENT = 5;

export interface ThreadLimits {
  /** Anchor-safe id, used for table row keys. */
  id: string;
  /** How the destination is named in the app. */
  destination: string;
  /** Which of the app's storage kinds share this row. */
  backends: string;
  uploadDefault: number;
  /** What the backend itself is willing to take. See `effectiveMax`. */
  uploadMax: number;
  restoreDefault: number;
  restoreMax: number;
  /** The one-line reason, for the cell beside the numbers. */
  why: string;
}

/**
 * The per-destination concurrency design: `ConcurrencyLimits` in the store
 * crate, surfaced to the desktop app as `concurrencyLimits()`.
 *
 * The four numbers in each row are cross-checked against those Rust constants
 * by `Providers.test.tsx`, which reads them out of the crate source and
 * matches them up by `id` — so a bound changed in the engine fails the portal
 * suite rather than leaving this table quoting numbers no run will honour.
 * The prose is this file's own and has no counterpart to check.
 *
 * The order is the order the page presents them in: the ones that hold
 * nothing back first, then the sync providers from most to least tolerant.
 */
export const THREAD_LIMITS: ThreadLimits[] = [
  {
    id: "direct",
    destination: "Local folder, NAS or network share",
    backends: "Folder, network share",
    uploadDefault: 4,
    uploadMax: 16,
    restoreDefault: 4,
    restoreMax: 16,
    why: "Nothing meters requests; the disk or the link is the limit, and the engine's own ceiling already covers that.",
  },
  {
    id: "object-store",
    destination: "S3-compatible object store",
    backends: "Backblaze B2, AWS S3, Wasabi, MinIO, Ceph",
    uploadDefault: 8,
    uploadMax: 32,
    restoreDefault: 8,
    restoreMax: 32,
    why: "Built for thousands of requests a second per prefix. A home link saturates long before the bucket notices, so the ceiling only keeps the number finite.",
  },
  {
    id: "google-drive",
    destination: "Google Drive",
    backends: "Google Drive (sign in)",
    uploadDefault: 8,
    uploadMax: 16,
    restoreDefault: 6,
    restoreMax: 16,
    why: "Quota is counted in requests per minute rather than enforced by a lock, so a pool that stays inside it runs at full width. Drive also caps uploads per user per day, which no thread count changes.",
  },
  {
    id: "onedrive",
    destination: "OneDrive",
    backends: "OneDrive (sign in)",
    uploadDefault: 4,
    uploadMax: 16,
    restoreDefault: 4,
    restoreMax: 16,
    why: "Microsoft Graph throttles per app and per user, and how hard depends on what else the tenant is doing. Its own guidance is to keep concurrency low and back off.",
  },
  {
    id: "dropbox",
    destination: "Dropbox",
    backends: "Dropbox (sign in)",
    uploadDefault: 4,
    uploadMax: 16,
    restoreDefault: 4,
    restoreMax: 16,
    why: "Dropbox serialises writes to a folder. Small packs go as single uploads that take that lock, so extra uploaders mostly queue on Dropbox's side. Reads never take it.",
  },
];

/** The ceiling a run will actually honour: the lower of the destination's own
 * and the engine's, exactly as `clamp_upload_concurrency_for` and
 * `clamp_restore_concurrency_for` take it. An object store's 32 is a statement
 * of what it would tolerate, not a number anyone can run. */
export function effectiveMax(destinationMax: number): number {
  return Math.min(ENGINE_CEILING, Math.max(1, destinationMax));
}

export interface TransferMechanics {
  destination: string;
  /** How one pack travels. */
  upload: string;
  /** The size at which the mechanism changes, if it does. */
  threshold: string;
}

/**
 * Where each backend switches from one request per pack to something chunked.
 * The thresholds are the store constants: `DEFAULT_MULTIPART_THRESHOLD` and
 * `DEFAULT_PART_SIZE` in the S3 store, `SIMPLE_UPLOAD_LIMIT` and `CHUNK_SIZE`
 * in OneDrive's, `SESSION_THRESHOLD` and `CHUNK_SIZE` in Dropbox's. Google
 * Drive has none: every pack is one request there.
 */
export const TRANSFER_MECHANICS: TransferMechanics[] = [
  {
    destination: "S3-compatible",
    upload: "One PUT per pack; above the threshold, a multipart upload in 16 MiB parts, aborted rather than abandoned on failure",
    threshold: "128 MiB",
  },
  {
    destination: "Google Drive",
    upload: "One request per pack, whatever its size",
    threshold: "none",
  },
  {
    destination: "OneDrive",
    upload: "A simple upload up to 4 MiB; otherwise an upload session in 10 MiB chunks",
    threshold: "4 MiB (so every pack is a session)",
  },
  {
    destination: "Dropbox",
    upload: "One upload per pack, which takes the folder's write lock; above the threshold, an upload session in 32 MiB chunks, which does not",
    threshold: "140 MiB",
  },
];

/**
 * The estimator's backup figure, in MiB. Follows the accounting in the
 * engine's `upload.rs`: the queue is a rendezvous, so in flight is bounded by
 * the worker count; sealing reserves the whole output buffer before freeing
 * any plaintext, so the peak is briefly twice the steady state; and the
 * producer holds its own two packs under construction, the data one at full
 * size and the metadata one at its fixed size. Parity adds a smaller third
 * term proportional to the percentage.
 */
export function backupMemoryMiB(
  workers: number,
  packMiB: number,
  parityPercent: number = DEFAULT_PARITY_PERCENT,
): { steady: number; peak: number } {
  const w = clampWorkers(workers);
  const pack = clampPack(packMiB);
  const producer = pack + META_PACK_MIB;
  const steady = w * pack + producer;
  const parity = (w * pack * parityPercent) / 100;
  const peak = 2 * w * pack + producer + parity;
  return { steady, peak };
}

/**
 * The estimator's restore figure, in MiB. Follows `fanout.rs`: every worker
 * holds one sealed data pack and one sealed metadata pack in its fetcher, and
 * so does the producer that feeds them, hence the `+ 1`; then one decrypted
 * chunk and one queue of chunk ids per worker, about a MiB each. Verify runs
 * the same fan-out and so costs the same.
 */
export function restoreMemoryMiB(workers: number, packMiB: number): number {
  const w = clampWorkers(workers);
  const pack = clampPack(packMiB);
  return (w + 1) * (pack + META_PACK_MIB) + 2 * w;
}

export function clampWorkers(n: number): number {
  if (!Number.isFinite(n)) return 4;
  return Math.min(ENGINE_CEILING, Math.max(1, Math.round(n)));
}

export function clampPack(mib: number): number {
  if (!Number.isFinite(mib)) return DEFAULT_PACK_MIB;
  return Math.min(MAX_PACK_MIB, Math.max(MIN_PACK_MIB, Math.round(mib)));
}

/** Render a MiB figure the way the page wants it: whole MiB below a GiB,
 * one decimal of GiB above. Not `humanBytes` from the shared package, which
 * gives a decimal at every unit; an estimate to the tenth of a MiB would
 * claim a precision the formulas above do not have. */
export function formatMiB(mib: number): string {
  if (mib < 1024) return `${Math.round(mib)} MiB`;
  return `${(mib / 1024).toFixed(1)} GiB`;
}
